import React, { useState, useEffect } from 'react';
import { startCase, uniq } from 'lodash';
import { Empty } from 'antd';
import { ResponsiveContainer, BarChart, CartesianGrid, Legend, XAxis, YAxis, Bar } from 'recharts';
import { Formatter } from '../../../utils';
import getCustomTooltip from './CustomTooltip';
import {
  COLORS,
  mapSumsToAxis,
  isDateGroup,
  normalizeDataByDate,
  normalizeNameFields,
} from './graphUtils';
import './styles.scoped.css';

const Tooltip = getCustomTooltip();

export interface BarGraphProps {
  data: any;
  sums: string[];
  groups: string[];
  dateRange: [Date, Date];
  isFunnel?: boolean;
}

function BarGraph({ data, sums, groups, dateRange, isFunnel = false }: BarGraphProps) {
  const [graphData, setGraphData] = useState<any[]>([]);
  const [sumsArr, setSumsArr] = useState(sums);
  const [yAxisMap, setyAxisMap] = useState(mapSumsToAxis(sums));
  const group = isDateGroup(groups[0]) ? 'dateString' : groups[0];
  const [uniqueYAxis, setUniqueYAxis] = useState(uniq(Object.values(mapSumsToAxis(sums))));

  useEffect(() => {
    if (isFunnel && data.length) {
      setSumsArr((currSums) => currSums.sort((a, b) => data[0][b] - data[0][a]));
      setyAxisMap(mapSumsToAxis(sumsArr));
      setUniqueYAxis(uniq(Object.values(mapSumsToAxis(sumsArr))));
    }
    if (isDateGroup(groups[0])) {
      setGraphData(normalizeDataByDate(data, sumsArr, groups[0], dateRange));
    }
    else setGraphData(normalizeNameFields(data, groups[0]));
  }, [data, dateRange, groups, isFunnel, sumsArr]);

  if (!graphData.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <ResponsiveContainer height="100%" width="100%">
      <BarChart data={graphData} barSize={12}>
        <CartesianGrid strokeDasharray="2 2" />
        {Tooltip}
        <Legend
          iconType="circle"
          formatter={(val) => (
            <span className="legend-label">{Formatter.startCaseWithSpecialChars(val)}</span>
          )}
          iconSize={12}
        />
        <XAxis dataKey={group} tick={{ fontSize: 12 }} tickSize={8} scale="band" tickCount={6} />
        {uniqueYAxis.map((id) => (
          <YAxis key={`YAxis-${id}`} yAxisId={id} hide allowDecimals />
        ))}
        {sumsArr.map((sum, i) => (
          <Bar
            key={sum}
            type="monotone"
            yAxisId={yAxisMap[sum]}
            dataKey={sum}
            name={startCase(sum)}
            fill={isFunnel ? COLORS.Pie[i % COLORS.Pie.length] : COLORS.Bar[i % COLORS.Bar.length]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default BarGraph;
